import React, { ReactNode, createContext, useCallback, useMemo } from "react";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { Entitlement, UserProfileRoles } from "interfaces/userProfile";

interface UserProfileContextType {
  loading: boolean;
  error: boolean;
  currentUserId: number | null;
  roles: string[];
  refetchUserProfile: () => void;
}

interface EntitlementContextType {
  loading: boolean;
  error: boolean;
  entitlements: Entitlement[];
  hasEntitlements: (_entitlements: string[]) => boolean;
  hasEntitlement: (_entitlement: string) => boolean;
  refetchEntitlements: () => void;
}

/** contains user profile info of the current user */
export const UserProfileContext = createContext<UserProfileContextType>({
  /** whether the user profile is currently being loaded */
  loading: true,
  /** whether something went wrong while loading the user profile */
  error: false,
  /** the id of the current user, defaults to null */
  currentUserId: null,
  roles: [],
  /** function to refetch the user profile from the api */
  refetchUserProfile: () => {}
});
UserProfileContext.displayName = "UserProfileContext";

/**
 * Contains the entitlements of the current user
 */
export const EntitlementContext = createContext<EntitlementContextType>({
  /** whether the entitlements are currently being loaded */
  loading: true,
  /** whether something went wrong while loading the entitlements */
  error: false,
  /** a list of the entitlements that the current user has, defaults to empty list*/
  entitlements: [],
  /** function for checking if the current user has given entitlements */
  hasEntitlements: _entitlements => false,
  /** function for checking if the current user has a given entitlement */
  hasEntitlement: _entitlement => false,
  /** function to refetch the entitlements from the api */
  refetchEntitlements: () => {}
});
EntitlementContext.displayName = "EntitlementContext";

interface UserProfileProviderProps {
  children: ReactNode;
}

interface UserProfileResponse {
  id?: number;
  entitlements?: Entitlement[];
  roles?: UserProfileRoles;
}

interface UseFetchGetResponse {
  loading: boolean;
  error: boolean;
  response: UserProfileResponse | undefined;
  refetch: () => Promise<any>;
}

/** Setups up the user profile and entitlement contexts by fetching from the api */
export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({
  children
}) => {
  const { loading, error, response, refetch } = useFetchGetV2(
    "settings/user_profile/me"
  ) as UseFetchGetResponse;

  // TODO: Remove this function when all converted to using hasEntitlements
  const hasEntitlement = useCallback(
    (entitlementName: string) => {
      if (response && response.entitlements) {
        return response.entitlements.some(
          entitlement => entitlement.name === entitlementName
        );
      }
      return false;
    },
    [response]
  );

  const hasEntitlements = useCallback(
    (entitlementNames: string[]): boolean => {
      return (
        !!response &&
        entitlementNames.every(entitlementName =>
          response?.entitlements?.some(obj => obj.name === entitlementName)
        )
      );
    },
    [response]
  );

  // Extract the roles a user is member of from the response
  const roles = useMemo(() => {
    if (response?.roles) {
      return response.roles.member.map(role => role.name);
    }
    return [];
  }, [response]);

  return (
    <UserProfileContext.Provider
      value={{
        loading,
        error,
        currentUserId: response?.id ?? null,
        roles: roles,
        refetchUserProfile: refetch
      }}
    >
      <EntitlementContext.Provider
        value={{
          loading,
          error,
          entitlements: response?.entitlements ?? [],
          hasEntitlement,
          hasEntitlements,
          refetchEntitlements: refetch
        }}
      >
        {children}
      </EntitlementContext.Provider>
    </UserProfileContext.Provider>
  );
};
